
import { useState } from 'react';

export default function ResourceChecklist() {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const checklistItems = [
    {
      id: 1,
      number: '01',
      title: 'Office Space or Building Selection',
      subtitle: 'Choosing the right location impacts workflow, technology, and patient experience.',
      icon: 'ri-building-line',
      description: 'Choosing the right location impacts workflow, technology, and patient experience.',
      considerations: [
        'Medical zoning & compliance',
        'Existing cabling and infrastructure',
        'Power redundancy and server/IT closet space',
        'Space for imaging, exam rooms, and future growth',
      ],
      howWeHelp: 'We review the space from a technology and network readiness standpoint before you sign a lease.',
    },
    {
      id: 2,
      number: '02',
      title: 'Network Layout & Infrastructure',
      subtitle: 'Your network is the backbone of your practice.',
      icon: 'ri-router-line',
      description: 'Your network is the backbone of your practice.',
      considerations: [
        'Secure wired & wireless network design',
        'Network segmentation (clinical vs guest Wi-Fi)',
        'Access points for full coverage',
        'Network printers & scanners',
        'Firewall and secure remote access',
        'Redundancy planning',
      ],
      howWeHelp: 'We design a HIPAA-ready, scalable network tailored to healthcare workflows.',
    },
    {
      id: 3,
      number: '03',
      title: 'Internet Services (Fiber, Cable, Redundancy)',
      subtitle: 'Reliable internet is mission-critical.',
      icon: 'ri-global-line',
      description: 'Reliable internet is mission-critical.',
      considerations: [
        'Fiber (preferred where available)',
        'Cable (Spectrum, Comcast, etc.)',
        'Backup internet (5G / LTE failover)',
      ],
      howWeHelp: 'We evaluate providers, negotiate pricing, and ensure uptime and redundancy for clinical operations.',
    },
    {
      id: 4,
      number: '04',
      title: 'Phone Systems (VoIP & Unified Communications)',
      subtitle: 'Modern practices need more than desk phones.',
      icon: 'ri-phone-line',
      description: 'Modern practices need more than desk phones.',
      considerations: [
        'Cloud VoIP phone systems',
        'Call routing & after-hours coverage',
        'Call recording (where permitted)',
        'Texting & patient communications',
        'Multi-location support',
      ],
      howWeHelp: 'We deploy healthcare-friendly VoIP systems that integrate with your workflow and scale easily.',
    },
    {
      id: 5,
      number: '05',
      title: 'Printers, Scanners & Medical Devices',
      subtitle: 'Healthcare printing has unique requirements.',
      icon: 'ri-printer-line',
      description: 'Healthcare printing has unique requirements.',
      considerations: [
        'Secure multifunction printers',
        'Document scanners',
        'Label printers',
        'Print security & access control',
        'Cost-control and toner management',
      ],
      howWeHelp: 'We standardize devices, reduce printing costs, and secure PHI.',
    },
    {
      id: 6,
      number: '06',
      title: 'Fractional Healthcare Operations Manager (Optional)',
      subtitle: 'Not every practice needs a full-time operations executive.',
      icon: 'ri-user-settings-line',
      description: 'Not every practice needs a full-time operations executive.',
      considerations: [
        'Vendor management',
        'Process design',
        'Technology alignment',
        'Scaling and multi-site planning',
        'Compliance coordination',
      ],
      howWeHelp:
        'We partner with fractional healthcare operations professionals when needed to support leadership teams.',
      optional: true,
    },
    {
      id: 7,
      number: '07',
      title: 'Financing & Capital Planning',
      subtitle: 'Healthcare startups have unique financial needs.',
      icon: 'ri-money-dollar-circle-line',
      description: 'Healthcare startups have unique financial needs.',
      considerations: [
        'Equipment financing',
        'IT & hardware leasing',
        'Technology refresh planning',
        'Vendor payment structuring',
      ],
      howWeHelp: 'We connect you with trusted financing partners and help phase technology investments intelligently.',
    },
    {
      id: 8,
      number: '08',
      title: 'Website & Patient Experience',
      subtitle: 'Your website is often the first impression.',
      icon: 'ri-window-line',
      description: 'Your website is often the first impression.',
      considerations: [
        'HIPAA-aware hosting',
        'Secure forms & patient portals',
        'Online scheduling',
        'SEO & local visibility',
        'Accessibility considerations',
      ],
      howWeHelp: 'We work alongside web and marketing partners to ensure your site is secure, fast, and compliant.',
    },
    {
      id: 9,
      number: '09',
      title: 'Marketing & Practice Growth',
      subtitle: 'Technology and marketing must align.',
      icon: 'ri-megaphone-line',
      description: 'Technology and marketing must align.',
      considerations: [
        'HIPAA-safe email & communications',
        'CRM & patient engagement tools',
        'Reputation management',
        'Analytics & reporting',
      ],
      howWeHelp: 'We ensure your marketing tools are secure and integrate cleanly with your systems.',
    },
  ];

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What You Need to Start or Launch a Healthcare Practice
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Below is a comprehensive checklist we help physician groups plan, design, and deploy.
          </p>
        </div>

        <div className="space-y-4">
          {checklistItems.map((resource, index) => (
            <div
              key={index}
              className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-red-300 transition-all"
            >
              <button
                onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                className="w-full px-8 py-6 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#e10708] to-[#c00607] rounded-lg flex items-center justify-center">
                    <i className={`${resource.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm font-bold text-[#e10708]">#{resource.number}</span>
                      <h3 className="text-xl font-bold text-gray-900">
                        {resource.title}
                      </h3>
                    </div>
                    <p className="text-gray-600">{resource.subtitle}</p>
                  </div>
                </div>
                <i
                  className={`ri-arrow-${expandedItem === index ? 'up' : 'down'}-s-line text-2xl text-gray-400`}
                ></i>
              </button>

              {expandedItem === index && (
                <div className="px-8 pb-8 pt-4 bg-gradient-to-br from-gray-50 to-white border-t border-gray-200">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-[#e10708]"></i>
                        Considerations:
                      </h4>
                      <ul className="space-y-3">
                        {resource.considerations.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <i className="ri-arrow-right-s-line text-[#e10708] text-xl flex-shrink-0 mt-0.5"></i>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-hand-heart-line text-[#e10708]"></i>
                        How we help:
                      </h4>
                      <div className="bg-red-50 border-l-4 border-[#e10708] p-6 rounded-lg">
                        <p className="text-gray-800 leading-relaxed font-medium">{resource.howWeHelp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
